// ═══════════════════════════════════════════════════════════════════════════
// Shared marketplace catalogue (backend-driven) for non-Connectors pages.
//
// ConnectorsPage owns its own fetch; this module exists so the other callers
// (AgentsPage, AgentMarketplacePage, CanvasPage, soarNodes.js) can share a
// single in-memory cache of the registry catalogue instead of each
// re-fetching or — as before — importing a hardcoded frontend constant.
//
// Contract:
//   fetchMarketplaceCatalogue()      → Promise<{connectors, categories}>,
//                                      deduplicated across concurrent callers.
//   getMarketplaceSnapshot()         → {connectors, categories} cached snapshot
//                                      (both empty arrays until first fetch
//                                      resolves).
//   subscribeMarketplace(fn)         → unsubscribe function. Every completed
//                                      fetch notifies subscribers with the
//                                      latest snapshot.
//   useMarketplaceCatalogue()        → React hook returning
//                                      {connectors, categories, loading, error}.
//                                      Auto-triggers the first fetch on mount.
//
// Side effects:
//   On every successful fetch, soarNodes' `registerConnectorRegistry` is
//   called with the new catalogue so the canvas node palette stays in sync
//   with the backend without any page-level coordination.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { listConnectorManifests, listConnectorCategories } from '../services/api';
import { buildMarketplaceFromRegistry } from './marketplaceFromRegistry';
import { registerConnectorRegistry } from './soarNodes';

let _snapshot = { connectors: [], categories: [] };
let _inFlight = null;
let _lastError = null;
const _listeners = new Set();

function _notify() {
  _listeners.forEach((fn) => {
    try { fn(_snapshot); } catch (e) { /* subscriber failure is isolated */ }
  });
}

/**
 * Returns the current cached snapshot. Before the first fetch resolves this
 * is {connectors: [], categories: []}. Callers that need freshness should
 * await `fetchMarketplaceCatalogue()` or subscribe.
 */
export function getMarketplaceSnapshot() {
  return _snapshot;
}

/**
 * Kick off (or reuse) a fetch of the backend catalogue. Concurrent callers
 * share the same in-flight promise. On resolution, subscribers are notified
 * and soarNodes' node palette is refreshed.
 */
export function fetchMarketplaceCatalogue({ force = false } = {}) {
  if (!force && _inFlight) return _inFlight;
  if (!force && _snapshot.connectors.length > 0) {
    return Promise.resolve(_snapshot);
  }

  // apiFetch wraps every response as {ok, data}. Unwrap .data (and guard with
  // Array.isArray) so buildMarketplaceFromRegistry receives the raw arrays it
  // expects — a wrapper object silently yields an empty marketplace otherwise.
  _inFlight = Promise.all([
    listConnectorManifests(),
    listConnectorCategories().catch(() => ({ ok: false, data: [] })),
  ])
    .then(([manifestsRes, categoriesRes]) => {
      const manifests = Array.isArray(manifestsRes?.data) ? manifestsRes.data
                       : Array.isArray(manifestsRes) ? manifestsRes
                       : [];
      const categories = Array.isArray(categoriesRes?.data) ? categoriesRes.data
                        : Array.isArray(categoriesRes) ? categoriesRes
                        : [];
      const built = buildMarketplaceFromRegistry(manifests, categories);
      _snapshot = built;
      _lastError = null;
      // Keep the canvas palette in sync with the registry.
      try { registerConnectorRegistry(built.connectors, built.categories); } catch (e) {
        console.warn('[marketplaceRegistry] registerConnectorRegistry failed', e);
      }
      _notify();
      return built;
    })
    .catch((err) => {
      _lastError = err;
      // Notify with current (empty) snapshot so `loading` consumers flip.
      _notify();
      throw err;
    })
    .finally(() => {
      _inFlight = null;
    });

  return _inFlight;
}

/**
 * Subscribe to catalogue updates. Fires on every successful fetch.
 * Returns an unsubscribe function.
 */
export function subscribeMarketplace(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * React hook for pages that want live catalogue state. Auto-fetches on first
 * mount; subsequent mounts reuse the cached snapshot.
 */
export function useMarketplaceCatalogue() {
  const [state, setState] = useState(() => ({
    connectors: _snapshot.connectors,
    categories: _snapshot.categories,
    loading: _snapshot.connectors.length === 0 && _inFlight === null,
    error: _lastError,
  }));

  useEffect(() => {
    let cancelled = false;
    const unsub = subscribeMarketplace((snap) => {
      if (cancelled) return;
      setState({ connectors: snap.connectors, categories: snap.categories, loading: false, error: _lastError });
    });

    // If we already have data, surface it; otherwise kick off a fetch.
    if (_snapshot.connectors.length > 0) {
      setState({ connectors: _snapshot.connectors, categories: _snapshot.categories, loading: false, error: _lastError });
    } else {
      setState((s) => ({ ...s, loading: true }));
      fetchMarketplaceCatalogue().catch(() => {
        if (!cancelled) {
          setState({ connectors: _snapshot.connectors, categories: _snapshot.categories, loading: false, error: _lastError });
        }
      });
    }

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return state;
}

/**
 * Non-React helper that returns the connectors array synchronously if loaded,
 * otherwise triggers a fetch and returns the (possibly empty) cached array.
 * Used by legacy `.find(...)` patterns being migrated off
 * MARKETPLACE_CONNECTORS.
 */
export function getMarketplaceConnectors() {
  if (_snapshot.connectors.length === 0 && _inFlight === null) {
    // Fire-and-forget so the second call returns real data.
    fetchMarketplaceCatalogue().catch(() => {});
  }
  return _snapshot.connectors;
}

export function getMarketplaceCategories() {
  if (_snapshot.categories.length === 0 && _inFlight === null) {
    fetchMarketplaceCatalogue().catch(() => {});
  }
  return _snapshot.categories;
}
